import type React from "react";

import type { XAxisProps, YAxisProps } from "recharts";
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { useTheme } from "styled-components";

import { TooltipContent, type TooltipContentProps } from "../TooltipContent";

import type { RoundedBarProps } from "./RoundedBar";
import { RoundedBar } from "./RoundedBar";

export type BarConfig = {
  dataKey: string;
  name: string;
  color: string;
};

interface StackedBarChartProps {
  bars: BarConfig[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data?: any[];
  height?: number;
  hideLegend?: boolean;
  tooltipProps?: TooltipContentProps;
  xAxisDataKey?: string;
  xAxisProps?: Omit<XAxisProps, "dataKey" | "ref">;
  yAxisDataKey?: string;
  yAxisProps?: Omit<YAxisProps, "dataKey" | "ref">;
}

export const StackedBarChart: React.FC<StackedBarChartProps> = ({
  bars,
  data,
  height,
  hideLegend,
  tooltipProps,
  xAxisDataKey,
  xAxisProps,
  yAxisDataKey,
  yAxisProps,
}) => {
  const theme = useTheme();

  return (
    <ResponsiveContainer width="100%" height={height || 450}>
      <BarChart
        data={data}
        margin={{
          top: 20,
          right: 0,
          left: 0,
          bottom: 5,
        }}
        maxBarSize={80}
      >
        <CartesianGrid vertical={false} stroke={theme.colors.redesign.db20} />
        <XAxis
          dataKey={xAxisDataKey || "date"}
          fontSize={14}
          tick={{ fill: theme.colors.redesign.db80 }}
          tickLine={false}
          fontWeight={500}
          dy={8}
          interval={0}
          stroke={theme.colors.redesign.db20}
          {...xAxisProps}
        />
        <YAxis
          dataKey={yAxisDataKey}
          tick={{ fill: theme.colors.redesign.db80 }}
          axisLine={false}
          tickLine={false}
          fontSize="14px"
          fontWeight={500}
          allowDecimals={false}
          {...yAxisProps}
        />
        <Tooltip
          content={tooltipProps ? <TooltipContent {...tooltipProps} /> : undefined}
          cursor={data && data?.length > 0 ? { fill: theme.colors.redesign.p20, fillOpacity: 0.25 } : false}
        />
        {!hideLegend && (
          <Legend
            verticalAlign="bottom"
            wrapperStyle={{ paddingTop: 24, display: "flex", gap: "16px", justifyContent: "center" }}
            iconType="circle"
            formatter={(value: string) => (
              <span style={{ color: theme.colors.redesign.db100, fontSize: 11, fontWeight: 500 }}>{value}</span>
            )}
          />
        )}
        {bars.map(bar => (
          <Bar
            key={bar.dataKey}
            dataKey={bar.dataKey}
            stackId="earnings"
            fill={bar.color}
            name={bar.name}
            shape={(shapeProps: unknown) => (
              <RoundedBar {...(shapeProps as Omit<RoundedBarProps, "bars">)} bars={bars} />
            )}
          />
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
};
